// Revenue (plan step 5.4; D52, D56): "how much did we make this month" without opening Stripe.
// MRR by plan and currency now, and the chosen month's movements: subscriptions started, ended
// (churn, with the MRR they carried) and set to cancel, trials and their conversion, what was
// collected, and failed payments. USD and BRL side by side, never converted (D52), with the BRL
// table's reference rate and its next review. Customer organizations only. Console D's Plan and
// usage (APS-412, admin-d.md "Receita / Margem"): a strip, tables per currency, `.kv` facts.
import { KV, PageHead, Section, Status, Strip } from '@apsio/ui'
import { useQuery } from '@tanstack/react-query'
import { useNavigate, useSearch } from '@tanstack/react-router'
import { type Revenue, revenueQuery } from '../api/queries'
import { DataTable, lastMonths, MonthChip } from '../components/kit'
import { Empty, ErrorState, Loading } from '../components/states'
import { type MessageId, useI18n } from '../i18n'
import { useFormat } from '../lib/format'
import { MoneyWarns } from './Margin'

type PlanRow = Revenue['mrr'][number]
type Movement = 'started' | 'ended' | 'canceling'

const CURRENCIES = ['usd', 'brl'] as const

const MOVEMENT: Record<Movement, MessageId> = {
  started: 'revenue.kind.started',
  ended: 'revenue.kind.ended',
  canceling: 'revenue.kind.canceling',
}

/** A list's amounts summed by currency, USD and BRL always, in that order. */
function byCurrency<T extends { currency: string }>(rows: T[], amount: (row: T) => number) {
  return CURRENCIES.map((currency) => ({
    currency,
    amount: rows.filter((r) => r.currency === currency).reduce((n, r) => n + amount(r), 0),
  }))
}

function Summary({ data }: { data: Revenue }) {
  const { t } = useI18n()
  const { money, moneyWhole, n, pct } = useFormat()
  const [usd, brl] = byCurrency(data.mrr, (r) => r.mrr)
  const count = (rows: PlanRow[]) => rows.reduce((sum, r) => sum + r.subscriptions, 0)
  const conversion = data.trials.ended > 0 ? data.trials.converted / data.trials.ended : null
  const lost = byCurrency(data.ended, (r) => r.mrr).filter((m) => m.amount > 0)
  const open = data.failed_payments.open
  return (
    <Strip
      label={t('revenue.title')}
      items={[
        {
          dt: t('revenue.mrr'),
          dd: money(usd?.amount ?? 0, 'usd'),
          d: money(brl?.amount ?? 0, 'brl'),
        },
        {
          dt: t('revenue.started'),
          dd: n(count(data.started)),
          d:
            conversion === null
              ? t('revenue.conversionNone')
              : t('revenue.conversionShort', { rate: pct(conversion) }),
        },
        {
          dt: t('revenue.ended'),
          dd: n(count(data.ended)),
          d:
            lost.length === 0
              ? t('revenue.churnNone')
              : t('revenue.churn', {
                  amount: lost.map((m) => moneyWhole(m.amount, m.currency)).join(' · '),
                }),
        },
        {
          dt: t('revenue.failed'),
          dtShort: t('revenue.failedShort'),
          dd: n(data.failed_payments.events),
          d:
            open.length === 0
              ? t('revenue.noneOpen')
              : t('revenue.openShort', {
                  amount: open.map((o) => moneyWhole(o.amount, o.currency)).join(' · '),
                }),
          // The one thing on this page to act on: a payment that failed.
          cur: data.failed_payments.events > 0,
        },
      ]}
    />
  )
}

function MrrTable({ rows }: { rows: PlanRow[] }) {
  const { t } = useI18n()
  const { money, n } = useFormat()
  if (rows.length === 0) return <Empty title={t('revenue.noMrr')} />
  return (
    <DataTable
      label={t('table.of', { name: t('revenue.mrrTitle') })}
      minWidth={420}
      className="money-fit"
    >
      <thead>
        <tr>
          <th className="f" scope="col">
            {t('revenue.col.plan')}
          </th>
          <th className="money-wide" scope="col">
            {t('revenue.col.currency')}
          </th>
          <th className="num money-wide" scope="col">
            {t('revenue.col.subscriptions')}
          </th>
          <th className="num last" scope="col">
            {t('revenue.col.mrr')}
          </th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={`${r.plan}-${r.currency}`}>
            <td className="f">
              {t(`plan.${r.plan}` as MessageId)}
              {/* Under 640 px the currency joins the plan and the count steps out (Mosaic). */}
              <span className="money-narrow" translate="no">
                {' · '}
                {r.currency.toUpperCase()}
              </span>
            </td>
            <td className="money-wide" translate="no">
              {r.currency.toUpperCase()}
            </td>
            <td className="num money-wide">{n(r.subscriptions)}</td>
            <td className="num last">{money(r.mrr, r.currency)}</td>
          </tr>
        ))}
      </tbody>
    </DataTable>
  )
}

/** The month's subscriptions, one table: started, ended and set to cancel, by plan and currency. */
function Movements({ data }: { data: Revenue }) {
  const { t } = useI18n()
  const { money, n, month } = useFormat()
  const rows = (['started', 'ended', 'canceling'] as const).flatMap((kind) =>
    data[kind].map((r) => ({ kind, ...r })),
  )
  if (rows.length === 0) return <Empty title={t('revenue.noMovements')} />
  return (
    <DataTable
      label={t('revenue.movementsTitle', { month: month(data.month) })}
      minWidth={520}
      className="money-fit"
    >
      <thead>
        <tr>
          <th className="f" scope="col">
            {t('revenue.col.kind')}
          </th>
          <th scope="col">{t('revenue.col.plan')}</th>
          <th className="money-wide" scope="col">
            {t('revenue.col.currency')}
          </th>
          <th className="num money-wide" scope="col">
            {t('revenue.col.subscriptions')}
          </th>
          <th className="num last" scope="col">
            {t('revenue.col.mrr')}
          </th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={`${r.kind}-${r.plan}-${r.currency}`}>
            <td className="f wrap">{t(MOVEMENT[r.kind])}</td>
            <td className="wrap">
              {t(`plan.${r.plan}` as MessageId)}
              {/* Under 640 px as in MRR now: the currency joins the plan, the count steps out. */}
              <span className="money-narrow" translate="no">
                {' · '}
                {r.currency.toUpperCase()}
              </span>
            </td>
            <td className="money-wide" translate="no">
              {r.currency.toUpperCase()}
            </td>
            <td className="num money-wide">{n(r.subscriptions)}</td>
            <td className="num last">{money(r.mrr, r.currency)}</td>
          </tr>
        ))}
      </tbody>
    </DataTable>
  )
}

/** What was collected and what is still open, per currency, never converted. */
function MoneyFacts({ data }: { data: Revenue }) {
  const { t } = useI18n()
  const { money, n, month } = useFormat()
  const line = (rows: Revenue['collected'], currency: string) => {
    const row = rows.find((r) => r.currency === currency)
    return row
      ? t('revenue.amountLine', { amount: money(row.amount, currency), invoices: n(row.invoices) })
      : money(0, currency)
  }
  return (
    <KV
      label={t('revenue.moneyTitle', { month: month(data.month) })}
      items={CURRENCIES.flatMap((c) => [
        [t('revenue.collectedIn', { currency: c.toUpperCase() }), line(data.collected, c)] as const,
        [
          t('revenue.openIn', { currency: c.toUpperCase() }),
          line(data.failed_payments.open, c),
        ] as const,
      ])}
    />
  )
}

/** The BRL table's reference rate and review (D52), and today's PTAX against it. */
function BrlFacts({ data }: { data: Revenue }) {
  const { t, date, number } = useI18n()
  const { pct } = useFormat()
  const rate = (r: number) =>
    number(r, { style: 'currency', currency: 'BRL', maximumFractionDigits: 4 })
  const ptax = data.ptax
  return (
    <>
      <MoneyWarns
        items={
          ptax.status === 'ok' && ptax.review_now
            ? [{ s: 'miss', text: t('revenue.driftReview') }]
            : []
        }
      />
      <KV
        label={t('revenue.brlTitle')}
        items={[
          [
            t('revenue.reference'),
            data.brl
              ? t('revenue.referenceValue', {
                  rate: rate(data.brl.reference_rate),
                  date: date(data.brl.reference_date),
                })
              : t('revenue.noBrl'),
          ],
          [t('revenue.nextReview'), data.brl ? date(data.brl.next_review) : t('common.none')],
          [
            t('revenue.ptaxToday'),
            ptax.status === 'ok' && ptax.rate !== null
              ? rate(ptax.rate)
              : t('revenue.ptaxUnavailable'),
          ],
          [
            t('revenue.drift'),
            ptax.status !== 'ok' || ptax.drift === null ? (
              t('common.none')
            ) : ptax.review_now ? (
              <Status key="drift" s="miss" glyph="!" label={pct(ptax.drift)} />
            ) : (
              pct(ptax.drift)
            ),
          ],
        ]}
      />
    </>
  )
}

export function RevenuePage() {
  const { t } = useI18n()
  const format = useFormat()
  const search = useSearch({ strict: false }) as { month?: string }
  const navigate = useNavigate()
  const revenue = useQuery(revenueQuery(search.month))
  const month = search.month ?? revenue.data?.month ?? lastMonths(new Date(), 1)[0] ?? ''
  return (
    <main id="main" className="pg">
      <PageHead
        title={t('revenue.title')}
        meta={t('revenue.lede')}
        actions={
          <MonthChip
            value={month}
            onChange={(m) => navigate({ to: '/revenue', search: { month: m } })}
          />
        }
      />
      {revenue.isPending ? (
        <Loading />
      ) : revenue.isError ? (
        <ErrorState error={revenue.error} retry={() => revenue.refetch()} />
      ) : (
        <>
          <Summary data={revenue.data} />
          <div className="grid2">
            <div className="sections">
              <Section title={t('revenue.mrrTitle')} meta={t('revenue.mrrSub')}>
                <MrrTable rows={revenue.data.mrr} />
                <div className="money-notes">
                  <p className="money-note">{t('revenue.leadNote')}</p>
                  <p className="money-note">{t('revenue.mrrNote')}</p>
                  {revenue.data.mrr_unknown > 0 ? (
                    <p className="money-note" role="status">
                      {t('revenue.unknown', { count: revenue.data.mrr_unknown })}
                    </p>
                  ) : null}
                </div>
              </Section>
              <Section
                title={t('revenue.movementsTitle', { month: format.month(revenue.data.month) })}
              >
                <Movements data={revenue.data} />
              </Section>
            </div>
            <div className="sections">
              <Section title={t('revenue.moneyTitle', { month: format.month(revenue.data.month) })}>
                <MoneyFacts data={revenue.data} />
              </Section>
              <Section title={t('revenue.brlTitle')}>
                <BrlFacts data={revenue.data} />
              </Section>
            </div>
          </div>
        </>
      )}
    </main>
  )
}
